import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Download,
  History,
  MessageSquare,
  Pencil,
  SendHorizontal,
} from 'lucide-react';
import { bpmnId, mapMetrics, parseBpmnId } from '@caliber-2/discovery-core/maps';
import {
  Callout,
  WorkspaceBanner,
  WorkspaceButton,
  WorkspaceLayout,
} from '@/components/ui/workspace';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import {
  applyChangeSet,
  discardChangeSet,
  downloadMapBpmn,
  isCurrentMetrics,
  processMapKeys,
  proposeChangeSet,
  redrawFutureMap,
  requestMapValidation,
  startMapDraft,
  undoMap,
  useCompareTableQuery,
  useMapChatQuery,
  useMapCompareQuery,
  useMapProcessesQuery,
  useMapVersionsQuery,
  usePainPointsQuery,
  useProcessMapQuery,
  useRefreshMap,
  useSendMapChatMutation,
  useValidatorsQuery,
  type FutureMapSummary,
  type MapProcess,
  type PickableEvidence,
} from '@/lib/api/process-maps';
import { useCanCurateFrameworks } from '@/lib/api/engagement-lifecycle';
import { saveBlob } from '@/lib/utils/download';
import { cn } from '@/lib/utils/cn';
import { useDiscoveryNavigate } from '../discovery-layout';
import {
  FRAMEWORKS_LINK_LABEL,
  NO_PROCESSES_TEXT,
  NO_PROCESSES_TITLE,
  noProcessesHelp,
} from '../discovery-logic';
import {
  ChatLauncher,
  FloatingPanel,
  FULL_SCREEN_FRAME,
  FullScreenToggle,
  useFocusLoop,
  useFullScreenEscape,
  ZoomButtons,
} from './full-screen';
import { MapCanvas, type MapCanvasHandle } from './map-canvas';
import { MapChatPanel, activeChatChangeSet } from './map-chat-panel';
import {
  bpmnFor,
  chatSuggestions,
  mergeMarkers,
  modelFromDto,
  participantName,
  plural,
  previewMarkers,
  proposedMap,
  refusalText,
  stepMarkers,
  undoLabel,
  type MapModel,
} from './map-logic';
import {
  EditBanner,
  MapLegend,
  MapMetricsRow,
  MapProvenance,
  MapStatusLine,
  PendingBanner,
  validationChip,
  type MapStatus,
} from './map-parts';
import { EngagementStepPanel, FutureStepPanel, RemovedStepPanel } from './step-panel';
import {
  CompareCanvases,
  ComparisonTable,
  FutureDrafting,
  FutureLegend,
  FutureNotReady,
  FuturePicker,
  LayoutSwitch,
  MapStateSwitch,
  ProductSelect,
  RedrawLine,
  VendorInfoLine,
  type FutureLayout,
  type MapState,
} from './future-parts';
import {
  futureFileName,
  futureMarkers,
  futureProposal,
  pairedKey,
  todayMarkers,
} from './future-logic';
import { useMapEditor } from './use-map-editor';
import { ValidateBar, VersionsPanel } from './versions-panel';

/**
 * Engagement -> Discovery -> Process maps (T-0189, E12-F09). Prototype:
 * `mapsView()`, `mapPanel()` and `chatPanel()` in
 * `docs/discovery/prototype/discovery.js` (route `#maps`, current state).
 *
 *   rail (processes, readiness)  |  toolbar, provenance, legend, canvas,
 *                                |  metrics                                |  chat / step / versions
 *
 * A map is a structured model; the API generates its BPMN, laid out by code.
 * Every change is a change set from chat, the canvas or the step panel,
 * checked by code and shown on the map, and changes nothing until Apply,
 * which writes a new version. Undo writes a new version too. Nothing sits
 * over the canvas: panels dock beside it and messages go in a line above it
 * (OQ-V).
 *
 * Full screen (5 Nov 2026, `full-screen.tsx`): the map takes the whole
 * window under its toolbar, without the rail. There the panel floats over
 * the corner of the canvas as a card and minimises to a Chat button, clear
 * of the bpmn.io watermark. The screen keeps the choice across the current
 * and future state of a process.
 *
 * T-0208 (E13-F09, ADR-020) adds the future state: the same map view for a
 * process drawn with a vendor product, with the server's change tags, the
 * vendor's evidence per step, Compare with today, the table of counts, and
 * newer vendor information offered as a redraft that changes nothing until
 * it is applied (Decision 62).
 */

export interface ProcessMapsScreenProps {
  engagementId: string;
  processId: string | null;
  onProcessChange: (processId: string) => void;
  /**
   * T-0208 — the future state shown: a vendor product's id, or `choose` for
   * the product picker. Null or absent shows the current state.
   */
  future?: string | null;
  onFutureChange?: (future: string | null) => void;
}

export function ProcessMapsScreen({
  engagementId,
  processId,
  onProcessChange,
  future = null,
  onFutureChange,
}: ProcessMapsScreenProps) {
  const q = useMapProcessesQuery(engagementId);
  const processes = React.useMemo(() => q.data ?? [], [q.data]);
  const selected =
    processes.find((p) => p.process_id === processId) ??
    processes.find((p) => p.map) ??
    processes.find((p) => p.ready) ??
    processes[0] ??
    null;
  // Full screen is for a drawn map: a process with none shows as usual.
  const [fullScreen, setFullScreen] = React.useState(false);
  const full = fullScreen && Boolean(selected?.map);
  const frameRef = React.useRef<HTMLDivElement>(null);
  useFocusLoop(full, frameRef);

  if (q.error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
        <ErrorMessage error={q.error} />
        <WorkspaceButton onClick={() => void q.refetch()}>Try again</WorkspaceButton>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading process maps…
      </div>
    );
  }
  if (processes.length === 0) return <NoProcessesDefined engagementId={engagementId} />;
  if (!processes.some((p) => p.map || p.ready)) {
    return <NothingToMap engagementId={engagementId} processes={processes} />;
  }

  const rail = full ? null : (
    <ProcessRail
      processes={processes}
      selectedId={selected?.process_id ?? null}
      onSelect={onProcessChange}
    />
  );
  let body: React.ReactNode;
  if (selected?.map) {
    const futures = selected.future_maps ?? [];
    const setState = (state: MapState) =>
      onFutureChange?.(state === 'current' ? null : (futures[0]?.vendor_product.id ?? 'choose'));
    body =
      future !== null ? (
        <FutureState
          engagementId={engagementId}
          process={selected}
          future={future}
          rail={rail}
          switcher={<MapStateSwitch value="future" onChange={setState} />}
          onFutureChange={(f) => onFutureChange?.(f)}
          fullScreen={full}
          onFullScreenChange={setFullScreen}
        />
      ) : (
        <MapView
          key={selected.map.id}
          engagementId={engagementId}
          process={selected}
          mapId={selected.map.id}
          rail={rail}
          toolbarStart={<MapStateSwitch value="current" onChange={setState} />}
          fullScreen={full}
          onFullScreenChange={setFullScreen}
        />
      );
  } else {
    body = (
      <WorkspaceLayout rail={rail} railLabel="Processes">
        {selected ? <NotDrawnYet engagementId={engagementId} process={selected} /> : null}
      </WorkspaceLayout>
    );
  }
  // One element in both states, so the map is not drawn again (and an edit
  // or a typed message is not lost) when full screen is switched.
  return (
    <div
      ref={frameRef}
      data-full-screen={full ? 'true' : undefined}
      {...(full ? { role: 'region', 'aria-label': 'Process map, full screen' } : {})}
      className={full ? FULL_SCREEN_FRAME : 'contents'}
    >
      {body}
    </div>
  );
}

/**
 * The pinned version defines no processes (C2-74): the same words as the
 * Overview, with the way to Firm settings for those who may add them.
 */
function NoProcessesDefined({ engagementId }: { engagementId: string }) {
  const go = useDiscoveryNavigate(engagementId);
  const canCurateFrameworks = useCanCurateFrameworks();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
      <h2 className="text-h2 font-semibold text-ink-900">{NO_PROCESSES_TITLE}</h2>
      <p className="max-w-md text-body-sm text-ink-500">
        {NO_PROCESSES_TEXT} {noProcessesHelp(canCurateFrameworks)}
      </p>
      {canCurateFrameworks ? (
        <WorkspaceButton className="mt-1" onClick={() => go({ to: 'frameworks' })}>
          {FRAMEWORKS_LINK_LABEL}
        </WorkspaceButton>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

export function processState(p: MapProcess): { tone: StatusChipTone; label: string } {
  if (p.map) {
    if (p.map.validation_state === 'validated') return { tone: 'healthy', label: 'Validated' };
    if (p.map.validation_state === 'requested')
      return { tone: 'amber', label: 'Awaiting validation' };
    if (p.map.validation_state === 'changes_requested')
      return { tone: 'at-risk', label: 'Client comments' };
    return { tone: 'amber', label: 'Draft' };
  }
  if (p.draft?.status === 'drafting') return { tone: 'ai', label: 'Drafting' };
  if (p.draft?.status === 'failed') return { tone: 'at-risk', label: 'Draft failed' };
  return p.ready
    ? { tone: 'ai', label: 'Ready to draft' }
    : { tone: 'unassessed', label: 'Needs evidence' };
}

function ProcessRail({
  processes,
  selectedId,
  onSelect,
}: {
  processes: MapProcess[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <p className="px-2 pb-2 text-meta text-ink-500">
        Processes come from the framework: each area lists the processes to map.
      </p>
      <ul>
        {processes.map((p) => {
          const st = processState(p);
          const active = p.process_id === selectedId;
          return (
            <li key={p.process_id}>
              <button
                type="button"
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelect(p.process_id)}
                className={cn(
                  'mb-0.5 flex w-full items-center gap-2 rounded-[6px] border border-transparent p-2 text-left text-body-sm hover:bg-paper-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  active &&
                    'border-ink-100 bg-paper-0 shadow-[inset_2px_0_0_var(--accent-amber-600)] hover:bg-paper-0',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-ink-900">{p.name}</span>
                  <span className="block truncate text-meta text-ink-500">
                    {p.area.label} · {plural(p.confirmed_evidence, 'confirmed item')}
                    {p.map && p.map.unconfirmed_steps > 0
                      ? ` · ${p.map.unconfirmed_steps} unconfirmed`
                      : ''}
                    {p.future_maps && p.future_maps.length > 0
                      ? ` · ${plural(p.future_maps.length, 'future state')}`
                      : ''}
                  </span>
                </span>
                <StatusChip tone={st.tone} size="sm">
                  {st.label}
                </StatusChip>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NothingToMap({
  engagementId,
  processes,
}: {
  engagementId: string;
  processes: MapProcess[];
}) {
  const go = useDiscoveryNavigate(engagementId);
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-[720px]">
        <div className="flex items-center gap-3 rounded-card border border-ink-100 bg-paper-50 px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <div className="text-body font-semibold text-ink-900">Nothing to map yet</div>
            <p className="text-meta text-ink-500">
              AI drafts a current-state map for each process once its area has confirmed evidence,
              every step cited.
            </p>
          </div>
          <WorkspaceButton onClick={() => go({ to: 'sessions' })}>Go to interviews</WorkspaceButton>
        </div>
        <div className="mt-4 text-meta font-semibold uppercase tracking-[0.06em] text-ink-500">
          Processes this framework maps
        </div>
        <ul className="mt-1.5">
          {processes.map((p) => (
            <li
              key={p.process_id}
              className="flex items-center gap-2.5 border-b border-ink-100 py-2 text-body-sm last:border-b-0"
            >
              <span className="min-w-0 flex-1 text-ink-900">{p.name}</span>
              <span className="text-meta text-ink-500">{p.area.label} · No evidence yet</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function NotDrawnYet({ engagementId, process }: { engagementId: string; process: MapProcess }) {
  const go = useDiscoveryNavigate(engagementId);
  const qc = useQueryClient();
  const [starting, setStarting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const st = processState(process);
  const drafting = process.draft?.status === 'drafting';
  const failed = process.draft?.status === 'failed';

  const draft = async () => {
    setStarting(true);
    setError(null);
    try {
      await startMapDraft(engagementId, process.process_id);
    } catch (err) {
      // A draft already running is fine: the list shows it.
      if (!(err instanceof ApiError && err.details?.['reason'] === 'map.draft_in_progress'))
        setError(writeError(err));
    } finally {
      setStarting(false);
      await qc.invalidateQueries({ queryKey: processMapKeys.processes(engagementId) });
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="flex max-w-md flex-col items-center gap-2 text-center">
        <StatusChip tone={st.tone}>{st.label}</StatusChip>
        <h2 className="text-h2 font-semibold text-ink-900">{process.name}</h2>
        {drafting ? (
          <p className="flex items-center gap-2 text-body-sm text-ink-700">
            <Spinner /> AI is drafting the map from the confirmed evidence, every step cited. It
            shows here when it is done.
          </p>
        ) : process.ready ? (
          <>
            <p className="text-body-sm text-ink-700">
              {process.area.label} has{' '}
              {plural(process.confirmed_evidence, 'confirmed evidence item')}. AI drafts the
              current-state map from it, every step cited, and code lays it out.
            </p>
            {failed ? (
              <Callout tone="warn" icon={AlertTriangle} className="text-left">
                The last draft did not finish
                {process.draft?.message ? `: ${process.draft.message}` : '.'} Nothing was kept.
              </Callout>
            ) : null}
            {error ? (
              <Callout tone="warn" icon={AlertTriangle} className="text-left">
                {error}
              </Callout>
            ) : null}
            <WorkspaceButton
              variant="primary"
              size="md"
              disabled={starting}
              onClick={() => void draft()}
            >
              {failed ? 'Try the draft again' : 'Draft this map with AI'}
            </WorkspaceButton>
          </>
        ) : (
          <>
            <p className="text-body-sm text-ink-700">
              AI drafts this map once {process.area.label} has confirmed evidence. Nothing is
              confirmed there yet.
            </p>
            <WorkspaceButton onClick={() => go({ to: 'findings', area: process.area.id })}>
              Go to {process.area.label}
            </WorkspaceButton>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The future state (T-0208)
// ---------------------------------------------------------------------------

/**
 * Future state for one process: not ready until the current state is
 * validated, the product picker, the first draft running, or the future map
 * itself (the same map view as the current state).
 */
function FutureState({
  engagementId,
  process,
  future,
  rail,
  switcher,
  onFutureChange,
  fullScreen,
  onFullScreenChange,
}: {
  engagementId: string;
  process: MapProcess;
  future: string;
  rail: React.ReactNode;
  switcher: React.ReactNode;
  onFutureChange: (future: string | null) => void;
  fullScreen: boolean;
  onFullScreenChange: (on: boolean) => void;
}) {
  const futures = process.future_maps ?? [];
  const chosen =
    future === 'choose' ? null : (futures.find((f) => f.vendor_product.id === future) ?? null);
  const drawn = chosen !== null && chosen.latest_version_no !== null;
  // The picker and the first draft have no map view, which handles Escape
  // for a drawn map: here Escape leaves full screen.
  useFullScreenEscape(fullScreen && !drawn, () => onFullScreenChange(false));
  const toolbarStart = (
    <>
      {switcher}
      {futures.length > 0 ? (
        <ProductSelect
          futures={futures}
          value={chosen?.vendor_product.id ?? 'choose'}
          onChange={(v) => onFutureChange(v)}
        />
      ) : null}
    </>
  );
  if (chosen && drawn) {
    return (
      <MapView
        key={chosen.map_id}
        engagementId={engagementId}
        process={process}
        mapId={chosen.map_id}
        rail={rail}
        toolbarStart={toolbarStart}
        future={chosen}
        fullScreen={fullScreen}
        onFullScreenChange={onFullScreenChange}
      />
    );
  }
  const body = chosen ? (
    <FutureDrafting engagementId={engagementId} process={process} future={chosen} />
  ) : process.current_validated_version_no == null ? (
    <FutureNotReady process={process} onBack={() => onFutureChange(null)} />
  ) : (
    <FuturePicker
      engagementId={engagementId}
      process={process}
      futures={futures}
      onOpen={(productId) => onFutureChange(productId)}
    />
  );
  return (
    <WorkspaceLayout
      rail={rail}
      railLabel="Processes"
      toolbar={
        fullScreen ? (
          <>
            {toolbarStart}
            <span className="flex-1" />
            <FullScreenToggle on onChange={onFullScreenChange} />
          </>
        ) : (
          toolbarStart
        )
      }
    >
      {body}
    </WorkspaceLayout>
  );
}

// ---------------------------------------------------------------------------
// One map
// ---------------------------------------------------------------------------

interface LocalProposal {
  id: string;
  origin: 'canvas' | 'manual';
  title: string;
  operations: Array<Record<string, unknown>>;
  summary: string[];
  evidence?: Map<string, { number: string | null; status: 'confirmed' }>;
  painPoints?: Map<string, { number: string | null; status: 'confirmed' }>;
}

type Dock = 'chat' | 'versions' | 'hidden';

function writeError(err: unknown): string {
  if (err instanceof ApiError) {
    const reason = err.details?.['reason'];
    if (reason === 'map.version_changed')
      return 'The map changed since this was proposed, so nothing was applied. Ask again on the latest version.';
    if (reason === 'map.invalid_change_set') return refusalText(err.message);
    return err.message;
  }
  return err instanceof Error ? err.message : 'That did not work.';
}

const FUTURE_EDIT_NOTE =
  'A step you add, or whose wording, lane, type or system you change, needs vendor evidence: until you link a knowledge item it is marked an assumption. Done editing checks your edits as one change for you to apply.';

function MapView({
  engagementId,
  process,
  mapId,
  rail,
  toolbarStart,
  future,
  fullScreen = false,
  onFullScreenChange,
}: {
  engagementId: string;
  process: MapProcess;
  mapId: string;
  rail: React.ReactNode;
  /** The state switch, and for a future state the product, at the start of the toolbar. */
  toolbarStart?: React.ReactNode;
  /** T-0208 — a future-state map: its line in the process list. */
  future?: FutureMapSummary;
  /** The map takes the whole window; the panel floats over the canvas (`full-screen.tsx`). */
  fullScreen?: boolean;
  onFullScreenChange?: (on: boolean) => void;
}) {
  const go = useDiscoveryNavigate(engagementId);
  const refresh = useRefreshMap(engagementId, mapId);
  const [viewNo, setViewNo] = React.useState<number | null>(null);
  const latestQ = useProcessMapQuery(engagementId, mapId, null);
  const olderQ = useProcessMapQuery(engagementId, mapId, viewNo);
  const versionsQ = useMapVersionsQuery(engagementId, mapId);
  const chatQ = useMapChatQuery(engagementId, mapId);
  const send = useSendMapChatMutation(engagementId, mapId);

  // Full screen opens on the map alone: the chat waits behind its button.
  const [dock, setDock] = React.useState<Dock>(fullScreen ? 'hidden' : 'chat');
  const canvasRef = React.useRef<MapCanvasHandle>(null);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [local, setLocal] = React.useState<LocalProposal | null>(null);
  const [status, setStatus] = React.useState<MapStatus | null>(null);
  const [validating, setValidating] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const validatorsQ = useValidatorsQuery(engagementId, validating);
  const painPointsQ = usePainPointsQuery(engagementId, selected !== null && !future);

  const latest = latestQ.data;
  const shown = viewNo === null ? latest : olderQ.data;
  const latestNo = latest?.version.no ?? 0;

  // T-0208 — a future map: Compare with today, the counts table, and the
  // current-state version it was drawn against (for the step panel).
  const [layout, setLayout] = React.useState<FutureLayout>('map');
  const [selectedToday, setSelectedToday] = React.useState<string | null>(null);
  const comparing = Boolean(future) && layout === 'compare';
  // Compare with today draws two canvases: there the panel stays docked.
  const floating = fullScreen && !comparing;
  /** Where the panel goes back to when a step or the versions are closed. */
  const restDock: Dock = floating ? 'hidden' : 'chat';
  const wasFullScreen = React.useRef(fullScreen);
  React.useEffect(() => {
    if (wasFullScreen.current === fullScreen) return;
    wasFullScreen.current = fullScreen;
    // In: the map alone, the chat behind its button. Out: the chat docks again.
    setDock((d) => (fullScreen ? (d === 'chat' ? 'hidden' : d) : d === 'hidden' ? 'chat' : d));
    // The space changed: fit the whole map to it.
    canvasRef.current?.fit();
  }, [fullScreen]);
  const tableQ = useCompareTableQuery(engagementId, process.process_id, Boolean(future));
  const compareQ = useMapCompareQuery(engagementId, mapId, viewNo, comparing);
  const todayNo = shown?.current_state_version_no ?? null;
  const todayQ = useProcessMapQuery(
    engagementId,
    process.map?.id ?? '',
    todayNo,
    Boolean(future) && todayNo !== null && selected !== null,
  );
  // A redraw writes the next version: look again when the list says it is there.
  const summaryNo = future?.latest_version_no ?? null;
  React.useEffect(() => {
    if (summaryNo !== null && latest && summaryNo > latest.version.no) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryNo]);
  const base = React.useMemo<MapModel>(
    () => (latest ? modelFromDto(latest) : { lanes: [], steps: [], flows: [] }),
    [latest],
  );
  const note = React.useCallback(
    (text: string, tone: 'default' | 'critical') => setStatus({ text, tone }),
    [],
  );
  const editor = useMapEditor(base, CANVAS_CONTEXT, note);

  const activeChat = activeChatChangeSet(chatQ.data, latest?.version.created_at ?? null);
  const proposal =
    editor.editing || viewNo !== null
      ? null
      : local
        ? { id: local.id, kind: 'local' as const, operations: local.operations }
        : activeChat
          ? { id: activeChat.id, kind: 'chat' as const, operations: activeChat.operations }
          : null;
  const proposed = React.useMemo(
    () =>
      proposal
        ? (future ? futureProposal : proposedMap)(base, proposal.operations, {
            origin: proposal.kind === 'chat' ? 'chat' : (local?.origin ?? 'manual'),
            user_id: null,
            ...(local?.evidence ? { evidence: local.evidence } : {}),
            ...(local?.painPoints ? { pain_points: local.painPoints } : {}),
          })
        : null,
    // The proposal is identified by its id; its operations never change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, proposal?.id],
  );

  const shownModel = React.useMemo(
    () => (shown && viewNo !== null ? modelFromDto(shown) : base),
    [shown, viewNo, base],
  );
  const drawn: MapModel = editor.editing ? editor.model : (proposed?.model ?? shownModel);
  const names = React.useMemo(
    () => ({
      process: latest?.map.name ?? process.name,
      participant: participantName(latest?.bpmn_xml ?? ''),
    }),
    [latest, process.name],
  );
  const xml = React.useMemo(
    () => (editor.editing || proposed ? bpmnFor(drawn, names) : (shown?.bpmn_xml ?? '')),
    [editor.editing, proposed, drawn, names, shown],
  );
  const markers = React.useMemo(
    () =>
      mergeMarkers(
        // A future map's marks are the server's tags; it computes them (rule 26).
        future
          ? futureMarkers(shown?.steps ?? [], shown?.vendor_information_changes)
          : stepMarkers(drawn, 'engagement'),
        proposed ? previewMarkers(proposed.preview) : {},
        selected ? { [bpmnId('step', selected)]: ['mk-sel'] } : {},
      ),
    [drawn, proposed, selected, future, shown],
  );
  const shownMetrics = shown?.metrics;
  const metrics = future
    ? undefined
    : editor.editing || proposed
      ? mapMetrics(drawn)
      : isCurrentMetrics(shownMetrics)
        ? shownMetrics
        : undefined;

  const validatedBefore = (versionsQ.data ?? []).some(
    (v) => v.validation_state === 'validated' && v.no < (shown?.version.no ?? 0),
  );

  // -- actions ---------------------------------------------------------------

  const savedText = (no: number, extra = '') =>
    `Map saved as version ${no}.${extra}${
      latest?.version.validation_state === 'requested'
        ? ` Version ${latestNo} was waiting for the client; that link no longer opens. Ask again when this one is ready.`
        : latest?.version.validation_state === 'validated' || validatedBefore
          ? ' The client validated an earlier version, so this one needs validating again.'
          : ''
    }`;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setStatus({ text: writeError(err), tone: 'critical' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const apply = (id: string) =>
    run(async () => {
      const r = await applyChangeSet(engagementId, mapId, id);
      setLocal(null);
      setSelected(null);
      setStatus({ text: savedText(r.version_no), tone: 'default' });
      await refresh();
    });

  const discard = (id: string) =>
    run(async () => {
      await discardChangeSet(engagementId, mapId, id);
      setLocal(null);
      setStatus({ text: 'Discarded. The map is unchanged.', tone: 'default' });
      await refresh();
    });

  const restore = (to: number) =>
    run(async () => {
      const r = await undoMap(engagementId, mapId, {
        to_version_no: to,
        base_version_no: latestNo,
      });
      setLocal(null);
      setViewNo(null);
      setSelected(null);
      setStatus({ text: savedText(r.version_no, ` It restores version ${to}.`), tone: 'default' });
      await refresh();
    });

  const proposeManual = (
    operations: Array<Record<string, unknown>>,
    evidence?: LocalProposal['evidence'],
    painPoints?: LocalProposal['painPoints'],
  ) =>
    run(async () => {
      const r = await proposeChangeSet(engagementId, mapId, {
        base_version_no: latestNo,
        origin: 'manual',
        operations,
      });
      if (!r.valid) {
        setStatus({ text: refusalText(r.invalid_reason), tone: 'critical' });
        return;
      }
      setLocal({
        id: r.change_set_id,
        origin: 'manual',
        title: 'Proposed change.',
        operations: r.operations,
        summary: r.summary ?? [],
        ...(evidence ? { evidence } : {}),
        ...(painPoints ? { painPoints } : {}),
      });
    });

  const startEditing = () =>
    run(async () => {
      const pending = local?.id ?? activeChat?.id ?? null;
      if (pending) {
        await discardChangeSet(engagementId, mapId, pending);
        setLocal(null);
        await refresh();
        setStatus({ text: 'Discarded the proposed change, to edit by hand.', tone: 'default' });
      } else setStatus(null);
      setSelected(null);
      setValidating(false);
      editor.start();
    });

  const doneEditing = () => {
    if (editor.batch.length === 0) {
      editor.stop();
      setStatus(null);
      return;
    }
    const issue = editor.issues[0];
    if (issue) {
      setStatus({
        text: `Can't save yet: ${issue.message} Connect it or remove it, then choose Done editing again.`,
        tone: 'critical',
      });
      return;
    }
    void run(async () => {
      const r = await proposeChangeSet(engagementId, mapId, {
        base_version_no: latestNo,
        origin: 'canvas',
        operations: editor.batch as unknown as Array<Record<string, unknown>>,
      });
      if (!r.valid) {
        setStatus({ text: refusalText(r.invalid_reason), tone: 'critical' });
        return;
      }
      editor.stop();
      setStatus(null);
      setLocal({
        id: r.change_set_id,
        origin: 'canvas',
        title: 'Your edits, as one change.',
        operations: r.operations,
        summary: r.summary ?? [],
      });
    });
  };

  const download = () =>
    run(async () => {
      const no = shown?.version.no ?? latestNo;
      const file = await downloadMapBpmn(
        engagementId,
        mapId,
        no,
        future ? futureFileName(process.name, future.vendor_product.name, no) : undefined,
      );
      saveBlob(file.fileName, file.blob);
      setStatus({
        text: `Downloaded ${file.fileName}, a BPMN 2.0 file. It opens in Camunda Modeler, Signavio and other BPMN tools.`,
        tone: 'default',
      });
    });

  const sendValidation = (personId: string) =>
    run(async () => {
      const r = await requestMapValidation(engagementId, mapId, latestNo, personId);
      setValidating(false);
      setStatus({
        text: `Secure link sent to ${r.person.name} for version ${r.version_no}. It shows only this map, read-only.`,
        tone: 'default',
      });
      await refresh();
    });

  const sendChat = async (text: string): Promise<boolean> => {
    try {
      await send.mutateAsync({ text, base_version_no: latestNo });
      setSelected(null);
      if (dock === 'hidden') setDock('chat');
      return true;
    } catch (err) {
      setStatus({ text: writeError(err), tone: 'critical' });
      return false;
    }
  };

  const onSelect = (elementId: string) => {
    if (editor.editing) return;
    const id = parseBpmnId(elementId);
    if (id?.kind !== 'step') return;
    const step = shown?.steps.find((s) => s.key === id.key);
    if (!step || step.type === 'start_event' || step.type === 'end_event') return;
    setSelected(step.key);
  };

  // -- the future state (T-0208) ------------------------------------------------

  /** Rule 25: newer vendor information becomes a change set, shown and applied like any other. */
  const redraft = () =>
    run(async () => {
      await send.mutateAsync({ redraft_affected: true, base_version_no: latestNo });
      setSelected(null);
      setSelectedToday(null);
      setLayout('map');
      setDock('chat');
      setStatus({
        text: 'The redraft of the affected steps is shown on the map. Nothing changes until you apply it.',
        tone: 'default',
      });
    });

  const redraw = () =>
    run(async () => {
      await redrawFutureMap(engagementId, mapId, latestNo);
      setStatus(null);
      await refresh();
    });

  /** Compare with today: a step on either side selects its pair on the other (same key). */
  const onSelectCompare = (side: 'today' | 'future') => (elementId: string) => {
    const id = parseBpmnId(elementId);
    const data = compareQ.data;
    if (id?.kind !== 'step' || !data) return;
    const steps = side === 'today' ? data.today.steps : data.future.steps;
    const step = steps.find((s) => s.key === id.key);
    if (!step || step.type === 'start_event' || step.type === 'end_event') return;
    const other = pairedKey(data.pairs, side, step.key);
    setSelected(side === 'future' ? step.key : other);
    setSelectedToday(side === 'today' ? step.key : other);
  };

  // -- layout -----------------------------------------------------------------

  // Escape leaves full screen; with a floating card open it closes the card (set below).
  const onEscape = React.useRef<() => void>(() => {});
  onEscape.current = () => onFullScreenChange?.(false);
  useFullScreenEscape(fullScreen, () => onEscape.current());

  if (latestQ.error || (viewNo !== null && olderQ.error)) {
    return (
      <WorkspaceLayout rail={rail} railLabel="Processes">
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
          <ErrorMessage error={latestQ.error ?? olderQ.error} />
          <WorkspaceButton onClick={() => setViewNo(null)}>Back to the latest</WorkspaceButton>
        </div>
      </WorkspaceLayout>
    );
  }
  if (!latest || !shown) {
    return (
      <WorkspaceLayout rail={rail} railLabel="Processes">
        <div className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading the map…
        </div>
      </WorkspaceLayout>
    );
  }

  const old = viewNo !== null && viewNo !== latestNo;
  const shownChip = validationChip(shown.version.validation_state, validatedBefore);
  const selectedStep = selected ? shown.steps.find((s) => s.key === selected) : undefined;
  const selectedRemoved =
    future && !selectedStep && selectedToday
      ? shown.removed_steps?.find((r) => r.key === selectedToday)
      : undefined;
  const todayDto = todayQ.data;
  const todayStep =
    future && selectedStep && todayDto
      ? todayDto.steps.find((s) => s.key === selectedStep.key)
      : undefined;
  const product = future?.vendor_product;
  const redrawing = future?.draft?.status === 'drafting';
  const busyReason = editor.editing
    ? 'Finish editing first.'
    : old
      ? 'Go back to the latest version first.'
      : local
        ? 'Apply or discard the change shown first.'
        : null;
  const undo = busyReason === null ? undoLabel(latestNo) : null;

  const panel =
    selectedStep && future && product ? (
      <FutureStepPanel
        key={selectedStep.key}
        step={selectedStep}
        laneName={shown.lanes.find((l) => l.key === selectedStep.lane)?.name ?? ''}
        today={
          todayStep && todayDto
            ? {
                name: todayStep.name,
                laneName: todayDto.lanes.find((l) => l.key === todayStep.lane)?.name ?? '',
                type: todayStep.type,
                system: todayStep.system,
              }
            : null
        }
        product={product}
        changes={(shown.vendor_information_changes ?? []).filter(
          (c) => c.step_key === selectedStep.key,
        )}
        canChange={busyReason ?? (proposal ? 'Apply or discard the change shown first.' : null)}
        onLinkItem={(item) =>
          void proposeManual([
            { op: 'link_vendor_knowledge', step: selectedStep.key, item: item.number },
          ])
        }
        onUnlinkItem={(number) =>
          void proposeManual([
            { op: 'unlink_vendor_knowledge', step: selectedStep.key, item: number },
          ])
        }
        onMarkAssumption={() =>
          void proposeManual([{ op: 'set_assumption', step: selectedStep.key, value: true }])
        }
        onClose={() => {
          setSelected(null);
          setSelectedToday(null);
        }}
      />
    ) : selectedRemoved && product ? (
      <RemovedStepPanel
        key={selectedRemoved.key}
        step={selectedRemoved}
        product={product}
        onClose={() => setSelectedToday(null)}
      />
    ) : selectedStep ? (
      <EngagementStepPanel
        key={selectedStep.key}
        engagementId={engagementId}
        areaId={latest.map.area_id}
        step={selectedStep}
        laneName={shown.lanes.find((l) => l.key === selectedStep.lane)?.name ?? ''}
        canChange={busyReason ?? (proposal ? 'Apply or discard the change shown first.' : null)}
        onLink={(item: PickableEvidence) =>
          void proposeManual(
            [{ op: 'link_evidence', step: selectedStep.key, evidence_id: item.id }],
            new Map([[item.id, { number: item.number, status: 'confirmed' }]]),
          )
        }
        onUnlink={(evidenceId) =>
          void proposeManual([
            { op: 'unlink_evidence', step: selectedStep.key, evidence_id: evidenceId },
          ])
        }
        painPoints={painPointsQ.data}
        onLinkPainPoint={(item) =>
          void proposeManual(
            [{ op: 'link_pain_point', step: selectedStep.key, pain_point_id: item.id }],
            undefined,
            new Map([[item.id, { number: item.number, status: 'confirmed' as const }]]),
          )
        }
        onUnlinkPainPoint={(painPointId) =>
          void proposeManual([
            { op: 'unlink_pain_point', step: selectedStep.key, pain_point_id: painPointId },
          ])
        }
        onFollowUp={() => go({ to: 'findings', area: latest.map.area_id })}
        onClose={() => setSelected(null)}
      />
    ) : dock === 'versions' ? (
      <VersionsPanel
        versions={versionsQ.data}
        error={versionsQ.error}
        latest={latestNo}
        viewing={shown.version.no}
        busy={busy || editor.editing}
        onView={(no) => {
          setSelected(null);
          setStatus(null);
          setViewNo(no === latestNo ? null : no);
        }}
        onRestore={(no) => void restore(no)}
        onClose={() => setDock(restDock)}
      />
    ) : dock === 'chat' && !comparing ? (
      <MapChatPanel
        versionLabel={`v${latestNo}`}
        thread={chatQ.data}
        error={chatQ.error}
        intro={
          product
            ? `Tell me what to change, in your own words. I'll show the change on the map first; nothing changes until you apply it. A step that says ${product.name} does something needs its evidence, or it is marked an assumption.`
            : "Tell me what to change, in your own words. I'll show the change on the map first; nothing changes until you apply it."
        }
        suggestions={
          product
            ? [
                ...chatSuggestions(base, 'engagement').filter((x) => !/unconfirmed/i.test(x)),
                'Which steps are assumptions?',
              ]
            : chatSuggestions(base, 'engagement')
        }
        disabledReason={busyReason}
        sending={send.isPending}
        activeId={proposal?.kind === 'chat' ? proposal.id : null}
        busy={busy}
        undo={undo ? { label: undo, onUndo: () => void restore(latestNo - 1) } : null}
        onSend={sendChat}
        onApply={(cs) => void apply(cs.id)}
        onDiscard={(cs) => void discard(cs.id)}
        onHide={() => setDock('hidden')}
        autoFocus={floating}
      />
    ) : null;

  // Escape on the full-screen map closes the floating card first, then leaves full screen.
  if (floating && panel) {
    onEscape.current = () => {
      setSelected(null);
      setSelectedToday(null);
      setDock('hidden');
    };
  }

  const toolbar = (
    <>
      {toolbarStart}
      {future ? (
        <LayoutSwitch
          value={layout}
          onChange={(l) => {
            if (editor.editing) {
              setStatus({ text: 'Finish editing first.', tone: 'critical' });
              return;
            }
            setLayout(l);
            setSelected(null);
            setSelectedToday(null);
          }}
        />
      ) : (
        <>
          <span className="text-body-sm font-semibold text-ink-900">{latest.map.name}</span>
          <span className="text-meta text-ink-500">{process.area.label} · current state</span>
        </>
      )}
      <span className="flex-1" />
      {old ? (
        <WorkspaceButton
          onClick={() => {
            setStatus(null);
            setViewNo(null);
          }}
        >
          Back to the latest
        </WorkspaceButton>
      ) : (
        <>
          <WorkspaceButton
            disabled={
              busy ||
              editor.editing ||
              shown.version.validation_state === 'validated' ||
              shown.version.validation_state === 'requested' ||
              Boolean(proposal)
            }
            title={
              editor.editing
                ? 'Finish editing first'
                : shown.version.validation_state === 'validated'
                  ? `Version ${latestNo} is validated`
                  : shown.version.validation_state === 'requested'
                    ? `Version ${latestNo} is waiting for the client`
                    : proposal
                      ? 'Apply or discard the change shown first'
                      : undefined
            }
            onClick={() => setValidating(true)}
          >
            <SendHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            Validate with client
          </WorkspaceButton>
          {dock === 'hidden' && !selectedStep && !comparing && !floating ? (
            <WorkspaceButton onClick={() => setDock('chat')}>
              <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
              Chat
            </WorkspaceButton>
          ) : null}
          <WorkspaceButton
            aria-pressed={dock === 'versions'}
            onClick={() => {
              setSelected(null);
              setDock(dock === 'versions' ? restDock : 'versions');
            }}
          >
            <History className="h-3.5 w-3.5" aria-hidden="true" />
            Versions
          </WorkspaceButton>
          {comparing ? null : (
            <WorkspaceButton
              disabled={busy}
              onClick={editor.editing ? doneEditing : () => void startEditing()}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              {editor.editing ? 'Done editing' : 'Edit'}
            </WorkspaceButton>
          )}
        </>
      )}
      <WorkspaceButton
        disabled={busy || editor.editing}
        title="Download as BPMN 2.0"
        onClick={() => void download()}
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        BPMN
      </WorkspaceButton>
      {floating ? (
        <ZoomButtons
          onZoomIn={() => canvasRef.current?.zoomIn()}
          onZoomOut={() => canvasRef.current?.zoomOut()}
          onFit={() => canvasRef.current?.fit()}
        />
      ) : null}
      {onFullScreenChange ? (
        <FullScreenToggle on={fullScreen} onChange={onFullScreenChange} />
      ) : null}
    </>
  );

  return (
    <WorkspaceLayout
      rail={rail}
      railLabel="Processes"
      toolbar={toolbar}
      panel={floating ? null : panel}
      panelMode="docked"
      footer={
        !future && metrics ? (
          <MapMetricsRow
            metrics={metrics}
            kind="engagement"
            proposed={Boolean(proposed) || editor.batch.length > 0}
          />
        ) : null
      }
    >
      <MapProvenance>
        <StatusChip tone={shownChip.tone}>{shownChip.label}</StatusChip>
        <span>
          {old ? `Version ${shown.version.no} of ${latestNo}. ` : `Version ${latestNo}. `}
          {shown.provenance_line}
          {future && shown.current_state_version_no
            ? ` · drawn against current state v${shown.current_state_version_no}`
            : ''}
        </span>
      </MapProvenance>
      {future && !old && !editor.editing ? (
        <>
          {(future.newer_current_validated || redrawing) && process.current_validated_version_no ? (
            <RedrawLine
              drawnAgainst={shown.current_state_version_no ?? future.drawn_against_version_no ?? 0}
              validatedNo={process.current_validated_version_no}
              redrawing={redrawing}
              busy={busy}
              disabledReason={
                busyReason ?? (proposal ? 'Apply or discard the change shown first.' : null)
              }
              onRedraw={() => void redraw()}
            />
          ) : null}
          {proposal ? null : (
            <VendorInfoLine
              changes={shown.vendor_information_changes ?? []}
              steps={shown.steps}
              busy={busy || send.isPending}
              disabledReason={
                busyReason ??
                (proposal
                  ? 'Apply or discard the change shown first.'
                  : redrawing
                    ? 'Wait for the redraw.'
                    : null)
              }
              onRedraft={() => void redraft()}
            />
          )}
        </>
      ) : null}
      {old ? (
        <div className="px-4 pt-2">
          <WorkspaceBanner
            actions={
              <WorkspaceButton disabled={busy} onClick={() => void restore(shown.version.no)}>
                Restore as version {latestNo + 1}
              </WorkspaceButton>
            }
          >
            You are looking at version {shown.version.no}. The latest is version {latestNo}.
          </WorkspaceBanner>
        </div>
      ) : null}
      {validating && !old ? (
        <ValidateBar
          versionNo={latestNo}
          validators={validatorsQ.data}
          loading={validatorsQ.isLoading}
          error={validatorsQ.error}
          busy={busy}
          onSend={(id) => void sendValidation(id)}
          onCancel={() => setValidating(false)}
        />
      ) : null}
      {editor.editing ? (
        <EditBanner
          reference={false}
          {...(future ? { note: FUTURE_EDIT_NOTE } : {})}
          onDiscard={() => {
            editor.stop();
            setStatus({ text: 'Edits discarded. The map is as it was.', tone: 'default' });
          }}
        />
      ) : null}
      {local && !editor.editing ? (
        <PendingBanner
          title={local.title}
          lines={local.summary}
          busy={busy}
          onApply={() => void apply(local.id)}
          onDiscard={() => void discard(local.id)}
        />
      ) : null}
      {future ? (
        <FutureLegend
          layout={layout}
          previewing={Boolean(proposed)}
          hint={
            editor.editing
              ? 'Drag, add, connect or delete; code lays the map out again.'
              : comparing
                ? 'Click a step on either side to see it on both.'
                : 'Click a step for the vendor’s evidence and the requirements it meets.'
          }
        />
      ) : (
        <MapLegend
          kind="engagement"
          previewing={Boolean(proposed)}
          hint={
            editor.editing
              ? 'Drag, add, connect or delete; code lays the map out again.'
              : 'Click a step for its evidence.'
          }
        />
      )}
      {future && !comparing && (shown.removed_steps?.length ?? 0) > 0 ? (
        <p className="px-4 pt-1.5 text-meta text-ink-500">
          Removed from today: {shown.removed_steps!.map((r) => `“${r.name}”`).join(', ')}.{' '}
          <button
            type="button"
            disabled={editor.editing}
            onClick={() => setLayout('compare')}
            className="font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900 disabled:opacity-60"
          >
            Compare with today
          </button>
        </p>
      ) : null}
      <MapStatusLine status={status} onDismiss={() => setStatus(null)} />
      {comparing && product ? (
        compareQ.data ? (
          <CompareCanvases
            compare={compareQ.data}
            productName={product.name}
            processName={latest.map.name}
            todayMarkers={mergeMarkers(
              todayMarkers(compareQ.data.today.steps),
              selectedToday ? { [bpmnId('step', selectedToday)]: ['mk-sel'] } : {},
            )}
            futureMarkers={mergeMarkers(
              futureMarkers(compareQ.data.future.steps, shown.vendor_information_changes),
              selected ? { [bpmnId('step', selected)]: ['mk-sel'] } : {},
            )}
            onSelectToday={onSelectCompare('today')}
            onSelectFuture={onSelectCompare('future')}
          />
        ) : compareQ.error ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
            <ErrorMessage error={compareQ.error} />
            <WorkspaceButton onClick={() => setLayout('map')}>Back to the map</WorkspaceButton>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading today beside the future state…
          </div>
        )
      ) : (
        <MapCanvas
          xml={xml}
          markers={markers}
          editable={editor.editing}
          redraw={editor.redraw}
          label={
            product
              ? `${latest.map.name} with ${product.name}, future-state map, version ${shown.version.no}`
              : `${latest.map.name}, current-state map, version ${shown.version.no}`
          }
          onSelect={onSelect}
          onCanvasChange={editor.onCanvasChange}
          handle={canvasRef}
          overlay={
            floating ? (
              panel ? (
                <FloatingPanel fill={!selected && !selectedToday && dock === 'chat'}>
                  {panel}
                </FloatingPanel>
              ) : (
                <ChatLauncher pending={proposal?.kind === 'chat'} onOpen={() => setDock('chat')} />
              )
            ) : undefined
          }
        />
      )}
      {future ? (
        // Below the canvas and scrolling with it, as in the prototype: a
        // fixed footer this tall left the canvas a sliver at 1440x900 (T-0192).
        <div className="flex flex-none flex-wrap gap-3.5 border-t border-ink-100 bg-paper-50 px-4 py-1.5 text-meta text-ink-500">
          <ComparisonTable
            table={tableQ.data}
            error={tableQ.error}
            shownMapId={mapId}
            evidence={
              product && shownMetrics && !isCurrentMetrics(shownMetrics)
                ? { product: product.name, versionNo: shown.version.no, metrics: shownMetrics }
                : null
            }
            note={
              editor.editing || proposed
                ? `The counts are for version ${latestNo}; they change when a change is applied.`
                : old
                  ? 'The counts are for the latest versions.'
                  : null
            }
          />
        </div>
      ) : null}
    </WorkspaceLayout>
  );
}

const CANVAS_CONTEXT = { origin: 'canvas' as const, user_id: null };
